import { useState } from 'react';
import {
  Stack, Paper, Group, Text, Table, Skeleton, Select, Modal, Timeline, Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle, IconMessageCircle } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import StatusBadge from '@/components/StatusBadge';
import { useDisputes, useDispute } from '@/hooks/usePortalApi';
import type { Dispute } from '@/types';
import dayjs from 'dayjs';

export default function DisputeTracker() {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedDisputeId, setSelectedDisputeId] = useState<number>(0);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  const { data: disputesData, isLoading: disputesLoading } = useDisputes({
    status: statusFilter || undefined,
  });

  const { data: selectedDispute, isLoading: disputeLoading } = useDispute(selectedDisputeId);

  const disputes = disputesData?.items ?? [];

  const handleRowClick = (dispute: Dispute) => {
    setSelectedDisputeId(dispute.id);
    openDetail();
  };

  const handleCloseDetail = () => {
    closeDetail();
    setSelectedDisputeId(0);
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const renderLoadingSkeleton = () => (
    <Paper p="md" withBorder>
      <Stack gap="xs">
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
      </Stack>
    </Paper>
  );

  const renderEmptyState = () => (
    <EmptyState
      icon={<IconAlertCircle size={48} />}
      title="No disputes found"
      description={
        statusFilter
          ? 'No disputes match your filter criteria.'
          : 'You have no billing disputes. Disputes can be submitted from invoice pages.'
      }
    />
  );

  const renderDisputesTable = () => {
    if (disputes.length === 0) {
      return renderEmptyState();
    }

    return (
      <Paper withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ticket #</Table.Th>
              <Table.Th>Subject</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Priority</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {disputes.map((dispute: Dispute) => (
              <Table.Tr
                key={dispute.id}
                style={{ cursor: 'pointer' }}
                onClick={() => handleRowClick(dispute)}
              >
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {dispute.ticket_number}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>
                    {dispute.subject}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={dispute.status} />
                </Table.Td>
                <Table.Td>
                  <Badge color={getPriorityColor(dispute.priority)} size="sm">
                    {dispute.priority}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{dayjs(dispute.created_at).format('MMM D, YYYY')}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  };

  const renderDetailModal = () => {
    if (!selectedDispute) return null;

    return (
      <Modal
        opened={detailOpened}
        onClose={handleCloseDetail}
        title={
          <Group gap="sm">
            <Text fw={600}>Dispute Details</Text>
            <StatusBadge status={selectedDispute.status} />
          </Group>
        }
        size="lg"
      >
        {disputeLoading ? (
          <Stack gap="md">
            <Skeleton height={30} />
            <Skeleton height={60} />
            <Skeleton height={200} />
          </Stack>
        ) : (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed">
                  Ticket #
                </Text>
                <Text fw={500}>
                  {selectedDispute.ticket_number}
                </Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">
                  Invoice
                </Text>
                <Text fw={500}>
                  {selectedDispute.related_invoice_id ? `#${selectedDispute.related_invoice_id}` : 'N/A'}
                </Text>
              </div>
              <div>
                <Text size="sm" c="dimmed">
                  Priority
                </Text>
                <Badge color={getPriorityColor(selectedDispute.priority)} size="sm">
                  {selectedDispute.priority}
                </Badge>
              </div>
            </Group>

            <div>
              <Text size="sm" c="dimmed" mb={4}>
                Subject
              </Text>
              <Text fw={500}>{selectedDispute.subject}</Text>
            </div>

            <div>
              <Text size="sm" c="dimmed" mb={4}>
                Description
              </Text>
              <Text size="sm">{selectedDispute.description}</Text>
            </div>

            {selectedDispute.timeline && selectedDispute.timeline.length > 0 && (
              <div>
                <Text size="sm" fw={500} mb="md">
                  Timeline
                </Text>
                <Timeline active={selectedDispute.timeline.length - 1} bulletSize={24}>
                  {selectedDispute.timeline.map((event, index) => (
                    <Timeline.Item
                      key={index}
                      bullet={
                        event.action === 'comment' ? (
                          <IconMessageCircle size={12} />
                        ) : (
                          <IconAlertCircle size={12} />
                        )
                      }
                      title={
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {event.action}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {dayjs(event.timestamp).format('MMM D, YYYY h:mm A')}
                          </Text>
                        </Group>
                      }
                    >
                      <Text size="sm" c="dimmed" mt={4}>
                        {event.description}
                      </Text>
                      {event.author && (
                        <Text size="xs" c="dimmed" mt={4}>
                          by {event.author}
                        </Text>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            )}

            {selectedDispute.resolved_at && (
              <Paper p="md" withBorder bg="gray.0">
                <Text size="sm" fw={500} mb={4}>
                  Resolved
                </Text>
                <Text size="xs" c="dimmed">
                  Resolved on {dayjs(selectedDispute.resolved_at).format('MMM D, YYYY')}
                </Text>
              </Paper>
            )}
          </Stack>
        )}
      </Modal>
    );
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Dispute Tracker"
        description="View and manage your billing disputes"
      />

      <Group>
        <Select
          placeholder="Filter by status"
          data={[
            { value: 'open', label: 'Open' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'closed', label: 'Closed' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          style={{ width: 200 }}
        />
      </Group>

      {disputesLoading ? renderLoadingSkeleton() : renderDisputesTable()}

      {renderDetailModal()}
    </Stack>
  );
}
